/**
 * Shopify Analytics native — events e-commerce envoyés à Shopify Admin.
 *
 * Pas d'analytics tiers (Mixpanel/GA4/Meta Pixel/Klaviyo) : on utilise
 * UNIQUEMENT le pipeline analytics natif de Shopify (Monorail) qui alimente
 * Shopify Admin → Analytics. Le marchand voit ses metrics directement
 * dans son admin sans dépendre de Google.
 *
 * Évents émis :
 *   - page_viewed         : à chaque chargement de page
 *   - product_viewed      : sur les pages produit (resolveProductHandleFromURL)
 *   - collection_viewed   : sur les pages collection (`/collection/...`)
 *   - search_submitted    : quand l'user soumet la search header
 *   - add_to_cart         : event smootify:added_to_cart
 *   - cart_viewed         : page cart visited
 *   - checkout_started    : redirect vers Shopify checkout
 *
 * RGPD : tous les fires sont gated par `analyticsAllowed()`. Si l'user
 * refuse → aucun event envoyé. Re-évalué dynamiquement via
 * `smootify:consent_changed` event.
 *
 * Mécanisme : POST direct vers Shopify Monorail endpoint
 * `https://monorail-edge.shopifysvc.com/v1/produce`. Pas besoin d'inclure
 * un script externe (analytics.js) qui ajouterait des cookies tiers.
 */

import { config } from "../config.js";
import { analyticsAllowed, attachConsentListener, whenAllowed } from "./consent.js";

const MONORAIL_ENDPOINT = "https://monorail-edge.shopifysvc.com/v1/produce";

interface MonorailEvent {
  schema_id: string;
  payload: Record<string, unknown>;
  metadata: {
    event_created_at_ms: number;
    event_sent_at_ms: number;
  };
}

function buildBaseMetadata(): MonorailEvent["metadata"] {
  const now = Date.now();
  return { event_created_at_ms: now, event_sent_at_ms: now };
}

function getShopId(): string {
  // Tente de lire depuis SmootifyUserOptions ou window.Shopify.
  if (typeof window !== "undefined" && window.Shopify) {
    const shopId = (window.Shopify as unknown as { shop?: string; shopId?: string })?.shopId;
    if (shopId) return String(shopId);
  }
  return ""; // Sans shopId → events seront ignorés par Monorail
}

function getCommonContext(): Record<string, unknown> {
  return {
    shop_id: getShopId(),
    page_url: typeof window !== "undefined" ? window.location.href : "",
    page_referrer: typeof document !== "undefined" ? document.referrer : "",
    page_title: typeof document !== "undefined" ? document.title : "",
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  };
}

async function sendEvent(schemaId: string, payload: Record<string, unknown>): Promise<void> {
  if (!analyticsAllowed()) return; // Belt and suspenders
  if (typeof fetch === "undefined") return;

  const event: MonorailEvent = {
    schema_id: schemaId,
    payload: { ...getCommonContext(), ...payload },
    metadata: buildBaseMetadata(),
  };

  try {
    // POST sans-await + mode no-cors (Monorail accepte sans CORS).
    void fetch(MONORAIL_ENDPOINT, {
      method: "POST",
      mode: "no-cors",
      keepalive: true,
      headers: { "Content-Type": "text/plain" }, // Évite preflight CORS
      body: JSON.stringify(event),
    });
  } catch {
    /* Silent — analytics ne doit jamais bloquer l'UX */
  }
}

// ---------------------------------------------------------------------------
//  Public events
// ---------------------------------------------------------------------------

export function trackPageView(): void {
  whenAllowed("analytics", () => {
    void sendEvent("custom_storefront_page_viewed/1.0", {
      event_name: "page_viewed",
    });
  });
}

interface ProductPayload {
  product_id?: string;
  product_handle: string;
  product_title?: string;
  product_vendor?: string;
  variant_id?: string;
  variant_price?: number;
  variant_currency?: string;
  quantity?: number;
}

export function trackProductView(p: ProductPayload): void {
  whenAllowed("analytics", () => {
    void sendEvent("custom_storefront_product_viewed/1.0", {
      event_name: "product_viewed",
      ...p,
    });
  });
}

export function trackAddToCart(p: ProductPayload): void {
  whenAllowed("analytics", () => {
    void sendEvent("custom_storefront_add_to_cart/1.0", {
      event_name: "add_to_cart",
      ...p,
    });
  });
}

export function trackCheckoutStarted(cart: {
  cart_id?: string;
  total?: number;
  currency?: string;
  item_count?: number;
}): void {
  whenAllowed("analytics", () => {
    void sendEvent("custom_storefront_checkout_started/1.0", {
      event_name: "checkout_started",
      ...cart,
    });
  });
}

export function trackSearch(query: string): void {
  whenAllowed("analytics", () => {
    void sendEvent("custom_storefront_search/1.0", {
      event_name: "search_submitted",
      search_query: query,
    });
  });
}

// ---------------------------------------------------------------------------
//  Auto-attach : listen smootify events + auto-fire analytics
// ---------------------------------------------------------------------------

let attached = false;

export function attachAnalytics(): void {
  if (attached) return;
  attached = true;
  if (typeof document === "undefined") return;

  // Bridge consent listener (Shopify visitorConsentCollected → notre event)
  attachConsentListener();

  // Page view au boot (si analytics allowed déjà OU dès qu'autorisé via whenAllowed)
  trackPageView();

  // Product view : detect via URL path /product/<handle>
  const urlMatch = window.location.pathname.match(
    new RegExp(`^/${config.productsBase}/([^/?#]+)`),
  );
  if (urlMatch && urlMatch[1]) {
    trackProductView({ product_handle: urlMatch[1] });
  }

  // Add to cart event Smootify
  document.addEventListener("smootify:added_to_cart", (ev) => {
    const detail = (ev as CustomEvent<{ lines?: Array<{ merchandiseId?: string; quantity?: number }>; cart?: unknown }>).detail;
    const line = detail?.lines?.[0];
    if (line?.merchandiseId) {
      trackAddToCart({
        product_handle: "",
        variant_id: line.merchandiseId,
        quantity: line.quantity,
      });
    }
  });

  // Checkout started : intercepte les redirections vers checkoutUrl Shopify
  // (ne couvre pas les buy-now click direct — déjà couvert via add_to_cart event)
  // Ici on écoute le smootify:cart_updated avec checkoutUrl pour log avant redirect
  // — option : on peut wrapper window.location.href sur les click "go to checkout"

  // Search submitted : listener sur le form predictive search
  document.addEventListener("submit", (ev) => {
    const form = ev.target as HTMLFormElement | null;
    if (!form || form.tagName !== "FORM") return;
    const input = form.querySelector<HTMLInputElement>('input[name="query"]');
    const q = input?.value?.trim();
    if (q && q.length >= 2 && form.action.includes("/search")) {
      trackSearch(q);
    }
  });
}
